import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { DateInput, ErrorMessage } from 'shouldve-been-mui';
import type ObservationModel from '../../Models/ObservationModelModel';
import type ObservationChapter from '../../Models/ObservationChapterModel';
import type ObservationSubChapter from '../../Models/ObservationSubChapterModel';
import FilledObservationLine from '../../Models/FilledObservationLineModel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import { updateLastActivity } from '../../Utils/LastActivity';
import Input from '@mui/material/Input';
import type { Theme} from '@mui/material';
import { Divider, OutlinedInput, useTheme } from '@mui/material';
import { parseNumberStrict } from 'shouldve-been-js';
import { handleTab } from 'shouldve-been-react';
import type ObservationCustomComponentModel from '../../Models/ObservationCustomComponentModelModel';
import ObservationCustomComponentType from '../../Enums/ObservationCustomComponentType';
import { dateToEightDigitString, eightDigitStringToDate } from '../../Utils/DateTimeParser';

type EditObservationLineDialogProps = {
    controller: boolean,
    onClose: (newObservationLine: FilledObservationLine) => void,
    observationLineId: string,
    chapters: ObservationChapter[],
    subChapters: ObservationSubChapter[],
    observationModel: ObservationModel,
    observationLines: FilledObservationLine[],
    ruleSetId: string,
};

function EditObservationLineDialog(props: Readonly<EditObservationLineDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const currentTheme: Theme = useTheme();

    const inputRefInspectorComment: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const inputRefGeneralComment: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const inputRefRootCause: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const inputRefCorrectiveAction: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const inputRefPreventiveAction: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const inputRefAdditionalComment: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());

    const [chapterId, setChapterId] = useState<string>('');
    const [subChapterId, setSubChapterId] = useState<string>('');
    const [priorityIndex, setPriorityIndex] = useState<number>(0);
    const [inspectorComment, setInspectorComment] = useState<string>('');
    const [generalComment, setGeneralComment] = useState<string>('');
    const [rootCause, setRootCause] = useState<string>('');
    const [correctiveAction, setCorrectiveAction] = useState<string>('');
    const [preventiveAction, setPreventiveAction] = useState<string>('');
    const [additionalComment, setAdditionalComment] = useState<string>('');
    const [custom1, setCustom1] = useState<string>('');
    const [custom2, setCustom2] = useState<string>('');
    const [custom3, setCustom3] = useState<string>('');
    const [custom4, setCustom4] = useState<string>('');
    const [createdDate, setCreatedDate] = useState<Date>(new Date(0));

    const [latestKeyEventInspectorComment, setLatestKeyEventInspectorComment] = useState<React.KeyboardEvent | null>(null);
    const [startPositionInspectorComment, setStartPositionInspectorComment] = useState<number | null>(null);
    const [latestKeyEventGeneralComment, setLatestKeyEventGeneralComment] = useState<React.KeyboardEvent | null>(null);
    const [startPositionGeneralComment, setStartPositionGeneralComment] = useState<number | null>(null);
    const [latestKeyEventRootCause, setLatestKeyEventRootCause] = useState<React.KeyboardEvent | null>(null);
    const [startPositionRootCause, setStartPositionRootCause] = useState<number | null>(null);
    const [latestKeyEventCorrectiveAction, setLatestKeyEventCorrectiveAction] = useState<React.KeyboardEvent | null>(null);
    const [startPositionCorrectiveAction, setStartPositionCorrectiveAction] = useState<number | null>(null);
    const [latestKeyEventPreventiveAction, setLatestKeyEventPreventiveAction] = useState<React.KeyboardEvent | null>(null);
    const [startPositionPreventiveAction, setStartPositionPreventiveAction] = useState<number | null>(null);
    const [latestKeyEventAdditionalComment, setLatestKeyEventAdditionalComment] = useState<React.KeyboardEvent | null>(null);
    const [startPositionAdditionalComment, setStartPositionAdditionalComment] = useState<number | null>(null);

    const selectedSubChapter: ObservationSubChapter | null = useMemo<ObservationSubChapter | null>(() => props.subChapters.find((currentSubChapter: ObservationSubChapter) => currentSubChapter.subChapterId === subChapterId) ?? null, [props.subChapters, subChapterId]);

    const chapterMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const filteredChapters: ObservationChapter[] = props.chapters.filter((chapter: ObservationChapter) => chapter.status === '-' && chapter.ruleSetId === props.ruleSetId);

        const sortedChapters: ObservationChapter[] = [...filteredChapters].sort((a: ObservationChapter, b: ObservationChapter) => a.sortNumber - b.sortNumber);

        return (
            [
                <MenuItem 
                    key='__empty__'
                    value=''
                >
                    &nbsp;
                </MenuItem>,
            ].concat(sortedChapters.map((chapter: ObservationChapter) => {
                return (
                    <MenuItem
                        key={chapter.chapterId}
                        value={chapter.chapterId}
                    >
                        {chapter.chapterNo + ' - ' + chapter.chapterName}
                    </MenuItem>
                );
            }))
        );
    }, [props.chapters, props.ruleSetId]);

    const subChapterMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const filteredSubChapters: ObservationSubChapter[] = props.subChapters.filter((subChapter: ObservationSubChapter) => subChapter.status === '-' && subChapter.ruleSetId === props.ruleSetId && (chapterId === '' || subChapter.chapterId === chapterId));

        const sortedSubChapters: ObservationSubChapter[] = [...filteredSubChapters].sort((a: ObservationSubChapter, b: ObservationSubChapter) => a.sortNumber - b.sortNumber);

        return (
            [
                <MenuItem 
                    key='__empty__'
                    value=''
                >
                    &nbsp;
                </MenuItem>,
            ].concat(sortedSubChapters.map((subChapter: ObservationSubChapter) => {
                return (
                    <MenuItem
                        key={subChapter.subChapterId}
                        value={subChapter.subChapterId}
                    >
                        {subChapter.itemNo + ' - ' + subChapter.name}
                    </MenuItem>
                );
            }))
        );
    }, [chapterId, props.ruleSetId, props.subChapters]);

    const priorityMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const sortedPriorityOptions: [number, string][] = [];

        for (let i: number = 0; i < props.observationModel.priorityVisualOrdering.length; i++) {

            const currentVisualOrderingValue: number | undefined = props.observationModel.priorityVisualOrdering[i];

            if (currentVisualOrderingValue === undefined) { //should not happen
                continue;    
            }

            const currentOptionValue: string | undefined = props.observationModel.priorityOptions[currentVisualOrderingValue];

            if (currentOptionValue === undefined) { //should not happen
                continue;
            }

            sortedPriorityOptions.push([currentVisualOrderingValue, currentOptionValue]);
        }

        const filteredPriorityOptions: [number, string][] = sortedPriorityOptions.filter((option: [number, string]) => props.observationModel.priorityItemEnableMask[option[0]] ?? true);

        return filteredPriorityOptions.map((priorityOption: [number, string]) => {
            return (
                <MenuItem
                    key={priorityOption[0]}
                    value={priorityOption[0].toString()}
                >
                    {priorityOption[1]}
                </MenuItem>
            );
        });
    }, [props.observationModel.priorityItemEnableMask, props.observationModel.priorityOptions, props.observationModel.priorityVisualOrdering]);

    const textAreaFields: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const inspectorCommentElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.inspectorCommentCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefInspectorComment.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={inspectorComment}
                        onChange={(e: React.ChangeEvent): void => setInspectorComment((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, inspectorComment, setInspectorComment, setLatestKeyEventInspectorComment, setStartPositionInspectorComment)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefInspectorComment}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const generalCommentElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.generalCommentCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefGeneralComment.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={generalComment}
                        onChange={(e: React.ChangeEvent): void => setGeneralComment((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, generalComment, setGeneralComment, setLatestKeyEventGeneralComment, setStartPositionGeneralComment)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefGeneralComment}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const rootCauseElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.rootCauseCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefRootCause.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={rootCause}
                        onChange={(e: React.ChangeEvent): void => setRootCause((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, rootCause, setRootCause, setLatestKeyEventRootCause, setStartPositionRootCause)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefRootCause}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const correctiveActionElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.correctiveActionCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefCorrectiveAction.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={correctiveAction}
                        onChange={(e: React.ChangeEvent): void => setCorrectiveAction((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, correctiveAction, setCorrectiveAction, setLatestKeyEventCorrectiveAction, setStartPositionCorrectiveAction)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefCorrectiveAction}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const preventiveActionElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.preventiveActionCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefPreventiveAction.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={preventiveAction}
                        onChange={(e: React.ChangeEvent): void => setPreventiveAction((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, preventiveAction, setPreventiveAction, setLatestKeyEventPreventiveAction, setStartPositionPreventiveAction)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefPreventiveAction}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const additionalCommentElement: JSX.Element = (
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    {props.observationModel.additionalCommentCaption}
                </span>
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRefAdditionalComment.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={additionalComment}
                        onChange={(e: React.ChangeEvent): void => setAdditionalComment((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, additionalComment, setAdditionalComment, setLatestKeyEventAdditionalComment, setStartPositionAdditionalComment)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRefAdditionalComment}
                    />
                </FormControl>
                <br />
                <br />
                <Divider />
            </>
        );

        const elementTuples: [boolean, number, JSX.Element][] = [
            [props.observationModel.inspectorCommentActive, props.observationModel.inspectorCommentSortNumber, inspectorCommentElement],
            [props.observationModel.generalCommentActive, props.observationModel.generalCommentSortNumber, generalCommentElement],
            [props.observationModel.rootCauseActive, props.observationModel.rootCauseSortNumber, rootCauseElement],
            [props.observationModel.correctiveActionActive, props.observationModel.correctiveActionSortNumber, correctiveActionElement],
            [props.observationModel.preventiveActionActive, props.observationModel.preventiveActionSortNumber, preventiveActionElement],
            [props.observationModel.additionalCommentActive, props.observationModel.additionalCommentSortNumber, additionalCommentElement],
        ];

        const filteredTuples: [boolean, number, JSX.Element][] = elementTuples.filter((tuple: [boolean, number, JSX.Element]) => tuple[0]);

        const sortedTuples: [boolean, number, JSX.Element][] = [...filteredTuples].sort((a: [boolean, number, JSX.Element], b: [boolean, number, JSX.Element]) => a[1] - b[1]);

        return sortedTuples.map((tuple: [boolean, number, JSX.Element]) => tuple[2]);

    }, [additionalComment, correctiveAction, currentTheme.palette.text.primary, generalComment, inspectorComment, preventiveAction, props.observationModel.additionalCommentActive, props.observationModel.additionalCommentCaption, props.observationModel.additionalCommentSortNumber, props.observationModel.correctiveActionActive, props.observationModel.correctiveActionCaption, props.observationModel.correctiveActionSortNumber, props.observationModel.generalCommentActive, props.observationModel.generalCommentCaption, props.observationModel.generalCommentSortNumber, props.observationModel.inspectorCommentActive, props.observationModel.inspectorCommentCaption, props.observationModel.inspectorCommentSortNumber, props.observationModel.preventiveActionActive, props.observationModel.preventiveActionCaption, props.observationModel.preventiveActionSortNumber, props.observationModel.rootCauseActive, props.observationModel.rootCauseCaption, props.observationModel.rootCauseSortNumber, rootCause, windowSize]);

    const customFields: JSX.Element = useMemo<JSX.Element>(() => {

        const customComponentElements: JSX.Element[] = [...props.observationModel.customComponents].map((customComponent: ObservationCustomComponentModel, index: number) => {

            const targetValue: string = 
                index === 0 ? custom1 : 
                    (index === 1 ? custom2 : 
                        (index === 2 ? custom3 : custom4));

            const targetSetter: (value: string) => void = 
                index === 0 ? setCustom1 : 
                    (index === 1 ? setCustom2 : 
                        (index === 2 ? setCustom3 : setCustom4));

            if (customComponent.type === ObservationCustomComponentType.COMBO) {

                return (
                    <>
                        <br />
                        <br />
                        <FormControl 
                            key={customComponent.sortNumber}
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>{customComponent.caption}</InputLabel>
                            <Select
                                value={targetValue}
                                onChange={(e: SelectChangeEvent): void => targetSetter((e.target as HTMLInputElement).value)}
                                size='small'
                                variant='outlined'
                                label={customComponent.caption}
                            >
                                {customComponent.options.filter((option: string, subIndex: number) => customComponent.itemEnableMask[subIndex] ?? true).map((option: string) => {
                                    return (
                                        <MenuItem
                                            key={option}
                                            value={option}
                                        >
                                            {option}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </>
                );
            } else { //date

                return (
                    <>
                        <br />
                        <br />
                        <DateInput
                            key={customComponent.sortNumber}
                            value={eightDigitStringToDate(targetValue)}
                            onChange={(value: Date | null): void => targetSetter(dateToEightDigitString(value))}
                            minWidth={Math.min(windowSize[0] * 0.8, 500)}
                            maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                            dense={true}
                            labelText={customComponent.caption}
                        />
                    </>
                );
            }
        });

        return props.observationModel.customComponents.length > 0 ?
            <>
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    Custom Fields
                </span>
                {customComponentElements}
                <Divider />
            </>
            : <></>;
    }, [currentTheme.palette.text.primary, custom1, custom2, custom3, custom4, props.observationModel.customComponents, windowSize]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        const relevantObservationLine: FilledObservationLine | undefined = props.observationLines.find((currentObservationLine: FilledObservationLine) => currentObservationLine.observationLineId === props.observationLineId);

        if (relevantObservationLine === undefined) { //should not happen
            setErrorMessage('Could not find observation line');
            return;
        }

        const relevantSubChapter: ObservationSubChapter | undefined = props.subChapters.find((currentSubChapter: ObservationSubChapter) => currentSubChapter.subChapterId === relevantObservationLine.subChapterId);

        if (relevantSubChapter !== undefined) {
            setChapterId(relevantSubChapter.chapterId);
        }

        setSubChapterId(relevantObservationLine.subChapterId);
        setPriorityIndex(relevantObservationLine.priority);
        setInspectorComment(relevantObservationLine.inspectorComment);
        setGeneralComment(relevantObservationLine.generalComment);
        setRootCause(relevantObservationLine.rootCause);
        setCorrectiveAction(relevantObservationLine.correctiveAction);
        setPreventiveAction(relevantObservationLine.preventiveAction);
        setAdditionalComment(relevantObservationLine.additionalComment);
        setCustom1(relevantObservationLine.custom1);
        setCustom2(relevantObservationLine.custom2);
        setCustom3(relevantObservationLine.custom3);
        setCustom4(relevantObservationLine.custom4);
        setCreatedDate(relevantObservationLine.createdDate);

    }, [props.observationLineId, props.observationLines, props.subChapters, rerenderFlag]);

    useEffect(() => {

        if (latestKeyEventInspectorComment !== null && startPositionInspectorComment !== null) {

            (latestKeyEventInspectorComment.target as HTMLInputElement).selectionStart = startPositionInspectorComment + 1;
            (latestKeyEventInspectorComment.target as HTMLInputElement).selectionEnd = startPositionInspectorComment + 1;
        }

    }, [inspectorComment, latestKeyEventInspectorComment, startPositionInspectorComment]);

    useEffect(() => {

        if (latestKeyEventGeneralComment !== null && startPositionGeneralComment !== null) {

            (latestKeyEventGeneralComment.target as HTMLInputElement).selectionStart = startPositionGeneralComment + 1;
            (latestKeyEventGeneralComment.target as HTMLInputElement).selectionEnd = startPositionGeneralComment + 1;
        }

    }, [generalComment, latestKeyEventGeneralComment, startPositionGeneralComment]);

    useEffect(() => {

        if (latestKeyEventRootCause !== null && startPositionRootCause !== null) {

            (latestKeyEventRootCause.target as HTMLInputElement).selectionStart = startPositionRootCause + 1;
            (latestKeyEventRootCause.target as HTMLInputElement).selectionEnd = startPositionRootCause + 1;
        }

    }, [rootCause, latestKeyEventRootCause, startPositionRootCause]);

    useEffect(() => {

        if (latestKeyEventCorrectiveAction !== null && startPositionCorrectiveAction !== null) {

            (latestKeyEventCorrectiveAction.target as HTMLInputElement).selectionStart = startPositionCorrectiveAction + 1;
            (latestKeyEventCorrectiveAction.target as HTMLInputElement).selectionEnd = startPositionCorrectiveAction + 1;
        }

    }, [correctiveAction, latestKeyEventCorrectiveAction, startPositionCorrectiveAction]);

    useEffect(() => {

        if (latestKeyEventPreventiveAction !== null && startPositionPreventiveAction !== null) {

            (latestKeyEventPreventiveAction.target as HTMLInputElement).selectionStart = startPositionPreventiveAction + 1;
            (latestKeyEventPreventiveAction.target as HTMLInputElement).selectionEnd = startPositionPreventiveAction + 1;
        }

    }, [preventiveAction, latestKeyEventPreventiveAction, startPositionPreventiveAction]);

    useEffect(() => {

        if (latestKeyEventAdditionalComment !== null && startPositionAdditionalComment !== null) {

            (latestKeyEventAdditionalComment.target as HTMLInputElement).selectionStart = startPositionAdditionalComment + 1;
            (latestKeyEventAdditionalComment.target as HTMLInputElement).selectionEnd = startPositionAdditionalComment + 1;
        }

    }, [additionalComment, latestKeyEventAdditionalComment, startPositionAdditionalComment]);

    function handleChangeSubChapter(newSubChapterId: string): void {

        updateLastActivity(userContext, localStorageContext);

        setSubChapterId(newSubChapterId);

        if (chapterId === '' && newSubChapterId !== '') {

            const relevantSubChapter: ObservationSubChapter | undefined = props.subChapters.find((currentSubChapter: ObservationSubChapter) => currentSubChapter.subChapterId === newSubChapterId);

            if (relevantSubChapter !== undefined) {
                setChapterId(relevantSubChapter.chapterId);
            }
        }
    }

    function handleClose(): void {

        const newObservationLine: FilledObservationLine = new FilledObservationLine(props.observationLineId, subChapterId, priorityIndex, inspectorComment, generalComment, rootCause, correctiveAction, preventiveAction, additionalComment, createdDate, custom1, custom2, custom3, custom4);

        setChapterId('');
        setSubChapterId('');
        setPriorityIndex(0);
        setInspectorComment('');
        setGeneralComment('');
        setRootCause('');
        setCorrectiveAction('');
        setPreventiveAction('');
        setAdditionalComment('');
        setCustom1('');
        setCustom2('');
        setCustom3('');
        setCustom4('');
        setCreatedDate(new Date(0));

        props.onClose(newObservationLine);
    }

    return (
        <Dialog 
            open={props.controller} 
            TransitionProps={{
                onEntered: (): void => setRerenderFlag(new Date()),
            }}
            PaperProps={{
                sx: {
                    position: 'fixed',
                },
            }}
        >
            <DialogTitle>Edit Observation Line</DialogTitle>
            <DialogContent>
                {props.observationModel.ruleSetsActive &&
                    <>
                        <span style={{
                            color: currentTheme.palette.text.primary,
                            fontWeight: 'bold',
                        }}>
                            Chapter
                        </span>
                        <br />
                        <br />
                        <FormControl 
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>Chapter</InputLabel>
                            <Select
                                value={chapterId}
                                onChange={(e: SelectChangeEvent): void => setChapterId((e.target as HTMLInputElement).value)}
                                size='small'
                                variant='outlined'
                                label='Chapter'
                            >
                                {chapterMenuItems}
                            </Select>
                        </FormControl>
                        <br />
                        <br />
                        <FormControl 
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>Subchapter</InputLabel>
                            <Select
                                value={subChapterId}
                                onChange={(e: SelectChangeEvent): void => handleChangeSubChapter((e.target as HTMLInputElement).value)}
                                size='small'
                                variant='outlined'
                                label='Subchapter'
                            >
                                {subChapterMenuItems}
                            </Select>
                        </FormControl>
                        <br />
                        <br />
                        <FormControl
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>Subchapter Name</InputLabel>
                            <OutlinedInput
                                type='text'
                                value={selectedSubChapter?.name ?? ''}
                                multiline={true}
                                label='Subchapter Name'
                                size='small'
                                disabled={true}
                                style={{
                                    color: currentTheme.palette.text.primary,
                                }}
                            />
                        </FormControl>
                        <br />
                        <br />
                        <FormControl
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>Subchapter Description</InputLabel>
                            <OutlinedInput
                                type='text'
                                value={selectedSubChapter?.description ?? ''}
                                multiline={true}
                                label='Subchapter Description'
                                size='small'
                                disabled={true}
                                style={{
                                    color: currentTheme.palette.text.primary,
                                }}
                            />
                        </FormControl>
                        <br />
                        <br />
                        <Divider />
                    </>
                }
                {props.observationModel.priorityActive &&
                    <>
                        <br />
                        <span style={{
                            color: currentTheme.palette.text.primary,
                            fontWeight: 'bold',
                        }}>
                            Priority
                        </span>
                        <br />
                        <br />
                        <FormControl 
                            size='small'
                            style={{
                                width: Math.min(windowSize[0] * 0.8, 500),
                            }}
                        >
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={priorityIndex.toString()}
                                onChange={(e: SelectChangeEvent): void => setPriorityIndex(parseNumberStrict((e.target as HTMLInputElement).value) ?? 0)}
                                size='small'
                                variant='outlined'
                                label='Priority'
                            >
                                {priorityMenuItems}
                            </Select>
                        </FormControl>
                        <br />
                        <br />
                    </>
                }
                {textAreaFields}
                {customFields}
                
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color='primary'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditObservationLineDialog;